import { confirmCost, type CostQuote } from '../features/studio/cost/confirmCost';
import {campaignLeaseHeader} from './campaignLease';
import { announceBillingBlock, billingBlockCode } from './billingGate';
import { productionGrantFor, bodyWithGrant } from './productionGrant';
import type {
  AuthState,
  GoogleSignInResponse,
  OnboardingPayload,
  InvitationPreview,
  MediaItem,
  MediaModelRecommendation,
  MfaChallengeResponse,
  CanvasBoard,
  CanvasBoardContent,
  CanvasComment,
  CanvasReaction,
  NotificationItem,
  Project,
  ProjectCollaborator,
  ProjectInvitation,
  ProjectJob,
  JobPriority,
  SchedulerBoard,
  RuntimeStatus,
  ReviewPresentation,
  ReviewLink,
  StudioMember,
  UsageSummary,
  KnowledgeDocument,
  KnowledgePassage,
  StudioInvitation,
  StudioMembership
} from './types';
import type { CraftCut, CraftEditorId, CraftInstallResult, CraftJobRequest, CraftRelease, CraftSessionOpen, CraftSessionSummary, CraftTitleSummary, CraftToolsResponse } from './crafts';
import type { PricingData, PurchaseRequest, RazorpayOrder, RazorpayVerification } from './pricing';
import type { BriefEstimateInput, CostReport, DraftOptions, EstimateCalibration, EstimateStage, FilmEstimate, FilmPlan, GenerationPrice, WritingPrice } from './estimate';
import type { SpatialAnchor, SpatialFrame, SpatialLocation } from '../../../server/intelligence/spatial/locations.js';

type SpatialLocationResult = { location: SpatialLocation; version: number; existing?: boolean };

export interface ApiErrorDetails {
  quote?: CostQuote;
  previousUpperInr?: number;
  shortfall?: { inr?: number; usd?: number };
}

export class ApiError extends Error {
  status: number;
  code?: string;
  quote?: CostQuote;
  previousUpperInr?: number;
  shortfall?: { inr?: number; usd?: number };

  constructor(message: string, status: number, code?: string, details: ApiErrorDetails = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.quote = details.quote;
    this.previousUpperInr = details.previousUpperInr;
    this.shortfall = details.shortfall;
  }
}

function csrfToken(): string | null {
  const cookie = document.cookie
    .split(';')
    .map(part => part.trim())
    .find(part => part.startsWith('cinema_csrf='));
  return cookie ? cookie.split('=').slice(1).join('=') : null;
}

function bodyWithCostApproval(body: BodyInit | null | undefined, token: string): BodyInit {
  if (typeof body === 'string') {
    return JSON.stringify({ ...JSON.parse(body || '{}'), costApproval: token });
  }
  if (body instanceof FormData) {
    const next = new FormData();
    body.forEach((value, key) => next.append(key, value));
    next.set('costApproval', token);
    return next;
  }
  return JSON.stringify({ costApproval: token });
}

async function retryAfterCostApproval<T>(
  path: string,
  options: RequestInit,
  quote: CostQuote,
  reviewCost: (quote: CostQuote) => Promise<boolean>
): Promise<T> {
  if (!(await reviewCost(quote))) {
    throw new ApiError('No paid work started.', 409, 'COST_DECLINED');
  }
  return request<T>(path, { ...options, body: bodyWithCostApproval(options.body, quote.token) }, true, reviewCost);
}

export async function request<T = unknown>(path: string, options: RequestInit = {}, approvedRetry = false, reviewCost: (quote: CostQuote) => Promise<boolean> = confirmCost): Promise<T> {
  const headers: Record<string, string> = {
    ...campaignLeaseHeader(path),
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...((options.headers as Record<string, string>) || {})
  };

  if (options.method && options.method !== 'GET' && options.method !== 'HEAD') {
    const token = csrfToken();
    if (token) headers['X-CSRF-Token'] = token;
  }

  const response = await fetch(path, { credentials: 'same-origin', ...options, headers });
  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok && payload.code === 'COST_APPROVAL_REQUIRED' && payload.quote && !approvedRetry) {
    const grant = productionGrantFor(path, options.body);
    if (grant) {
      try {
        return await request<T>(path, { ...options, headers: { ...((options.headers as Record<string, string>) || {}), 'Idempotency-Key': crypto.randomUUID() }, body: bodyWithGrant(String(options.body), grant.id) }, true);
      } catch (cause) {
        if (!(cause instanceof ApiError) || !String(cause.code || '').startsWith('GRANT_')) throw cause;
      }
    }
    return retryAfterCostApproval(path, options, payload.quote as CostQuote, reviewCost);
  }
  if (!response.ok) {
    const blocked = billingBlockCode(response.status, payload.code);
    if (blocked) announceBillingBlock({ code: blocked, message: String(payload.error || '') });
    throw new ApiError(
      String(payload.error || `Request failed (${response.status})`),
      response.status,
      payload.code ? String(payload.code) : undefined,
      {
        quote: payload.quote as CostQuote | undefined,
        previousUpperInr: typeof payload.previousUpperInr === 'number' ? payload.previousUpperInr : undefined,
        shortfall: payload.shortfall as ApiErrorDetails['shortfall']
      }
    );
  }
  return payload as T;
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body ?? {}) });
const post = (body?: unknown): RequestInit => ({ method: 'POST', ...json(body) });
const put = (body?: unknown): RequestInit => ({ method: 'PUT', ...json(body) });
const patch = (body?: unknown): RequestInit => ({ method: 'PATCH', ...json(body) });
const del = (): RequestInit => ({ method: 'DELETE' });
const encode = encodeURIComponent;

export const api = {
  /* ---------------------------------------------------------------- auth */
  authState: () => request<AuthState>('/api/auth/state'),
  bootstrap: (payload: { name: string; studioName: string; email: string; password: string }) =>
    request<{ ok: true }>('/api/auth/bootstrap', post(payload)),
  login: (payload: { email: string; password: string }) =>
    request<{ ok: true } | MfaChallengeResponse>('/api/auth/login', post(payload)),
  mfaLogin: (payload: { challenge: string; code: string }) =>
    request<{ ok: true; recoveryCodeUsed: boolean }>('/api/auth/login/mfa', post(payload)),
  signInWithGoogle: (payload: { credential: string; nonce: string; studioName?: string }) =>
    request<GoogleSignInResponse>('/api/auth/google', post(payload)),
  completeOnboarding: (payload: OnboardingPayload) => request<{ ok: true; name: string }>('/api/account/onboarding', post(payload)),
  signInWithSupabase: (payload: { accessToken: string; studioName?: string }) =>
    request<{ ok: true; user: { id: string; email: string; name: string; role: string; studio: string }; profileSynced: boolean }>(
      '/api/auth/supabase',
      post(payload)
    ),
  startSso: (payload: { email: string; returnPath: string }) =>
    request<{ authorizationUrl: string; studioName: string }>('/api/auth/sso/start', post(payload)),
  invitation: (token: string) => request<InvitationPreview>(`/api/auth/invitation?token=${encode(token)}`),
  acceptInvitation: (payload: { token: string; name: string; email: string; password: string }) =>
    request<{ ok: true }>('/api/auth/accept-invitation', post(payload)),
  logout: () => request<{ ok: true }>('/api/auth/logout', post()),
  account: () => request<{ user: Record<string, unknown> }>('/api/account'),

  /* ------------------------------------------------------------ projects */
  projects: (options: RequestInit = {}) => request<{ projects: Project[] } | Project[]>('/api/projects', options),
  getProject: (projectId: string) => request<Project>(`/api/projects/${encode(projectId)}`),
  createProject: (project: Partial<Project>) => request<Project>('/api/projects', post(project)),
  /* What reading a screenplay costs, before a project exists to charge it to. */
  screenplayQuote: (input: { text: string; filename?: string }) =>
    request<{ inr: number; usd: number; model: string; provider: string; characters: number; note: string }>(
      '/api/screenplay/quote', post(input)),
  estimateBrief: (input: BriefEstimateInput) => request<{ estimate: FilmEstimate }>('/api/estimate', post(input)),
  projectEstimate: (projectId: string, input: { stage?: EstimateStage; selectedPackage?: string; targetINR?: number; overrides?: BriefEstimateInput['overrides']; shotOverrides?: Record<string, { video?: string; resolution?: string; duration?: number }> } = {}) =>
    request<{ estimate: FilmEstimate }>(`/api/projects/${encode(projectId)}/estimate`, post(input)),
  filmPlan: (projectId: string) => request<{ plan: FilmPlan | null }>(`/api/projects/${encode(projectId)}/plan`),
  startFilmPlan: (projectId: string, input: { packageId: string; overrides?: BriefEstimateInput['overrides'] }) =>
    request<{ plan: FilmPlan; estimate: FilmEstimate }>(`/api/projects/${encode(projectId)}/plan`, post(input)),
  extendFilmPlan: (projectId: string, input: { byINR: number; writingByINR?: number }) =>
    request<{ plan: FilmPlan }>(`/api/projects/${encode(projectId)}/plan/extend`, post(input)),
  stopFilmPlan: (projectId: string) => request<{ plan: FilmPlan | null }>(`/api/projects/${encode(projectId)}/plan`, del()),
  writingPrice: (projectId: string, skillId = 'story-architect') => request<{ price: WritingPrice | null }>(`/api/projects/${encode(projectId)}/agent-price`, post({ skillId, stage: 'idea' })),
  generationPrice: (projectId: string, input: { modelId: string; shotId?: string; settings?: Record<string, unknown> }) =>
    request<{ price: GenerationPrice }>(`/api/projects/${encode(projectId)}/generations/price`, post(input)),
  draftOptions: (projectId: string, input: { shotId?: string; duration?: number } = {}) =>
    request<DraftOptions>(`/api/projects/${encode(projectId)}/generations/draft-options?${new URLSearchParams({ ...(input.shotId ? { shotId: input.shotId } : {}), ...(input.duration ? { duration: String(input.duration) } : {}) })}`),
  costReport: (projectId: string) => request<CostReport>(`/api/projects/${encode(projectId)}/cost-report`),
  calibrateEstimates: (projectId: string) => request<{ calibration: EstimateCalibration; observed: CostReport['observed'] }>(`/api/projects/${encode(projectId)}/cost-report/calibrate`, post({})),
  pricingCard: () => request<{ card: { capturedAt: string | null; models: unknown[] } | null; fxINRPerUSD: number; fxSource: string }>('/api/pricing/card'),
  saveProject: (project: Project) => request<Project>(`/api/projects/${encode(project.id)}`, put(project)),
  deleteProject: (projectId: string) => request<{ ok: true }>(`/api/projects/${encode(projectId)}`, del()),
  saveSchedule: (projectId: string, schedule: unknown) =>
    request<{ ok: true }>(`/api/projects/${encode(projectId)}/schedule`, put(schedule)),
  placeLegalHold: (projectId: string, payload: unknown) =>
    request(`/api/projects/${encode(projectId)}/legal-hold`, post(payload)),
  releaseLegalHold: (projectId: string, payload: unknown) =>
    request(`/api/projects/${encode(projectId)}/legal-hold/release`, post(payload)),
  extractScreenplay: (projectId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request(`/api/projects/${encode(projectId)}/screenplay/extract`, { method: 'POST', body });
  },
  extractScreenplayFile: (file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ text: string; filename: string; method?: string; warnings?: string[] }>('/api/screenplay/extract', { method: 'POST', body });
  },
  exportPackage: (projectId: string) => window.open(`/api/projects/${projectId}/export/package`, '_blank'),
  exportArchive: (projectId: string) => {
    const link = document.createElement('a');
    link.href = `/api/projects/${encode(projectId)}/export/archive`;
    link.download = '';
    document.body.append(link);
    link.click();
    link.remove();
  },
  importProjectArchive: (file: File) => {
    const body = new FormData();
    body.append('archive', file);
    return request<Project>('/api/projects/import/archive', { method: 'POST', body });
  },

  /* --------------------------------------------------------------- demos */
  globalGenerator: () => request<{ project: Project; jobs?: ProjectJob[] }>('/api/global-generator'),
  createDemoProduction: () => request('/api/demo-production', post()),
  resetDemoProduction: () => request('/api/demo-production/reset', post()),
  getDemoStatus: () => request('/api/demo-production'),
  createDemoMarketing: () => request('/api/demo-marketing', post()),
  resetDemoMarketing: () => request('/api/demo-marketing/reset', post()),

  /* -------------------------------------------------------------- agents */
  agent: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/agents/run`, post(payload)),
  researchMarketingTrends: (projectId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/marketing/research`, post(payload)),
  classifyMarketingAsset: (projectId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/marketing/classify-asset`, post(payload)),

  /* --------------------------------------------------------- craft tools */
  craftTools: (refresh = false) => request<CraftToolsResponse>(`/api/crafts${refresh ? '?refresh=1' : ''}`),
  craftRelease: (toolId: string) => request<CraftRelease>(`/api/crafts/${encode(toolId)}/release`),
  installCraftTool: (toolId: string, asset: string) =>
    request<CraftInstallResult>(`/api/crafts/${encode(toolId)}/install`, post({ asset })),
  prepareCraftEditor: (tool: CraftEditorId) =>
    request<{ tool: CraftEditorId; version: string; path: string; isolation: { mode: string; origin: string | null } }>(`/api/crafts/web/${encode(tool)}/prepare`, post()),
  craftCut: (projectId: string) => request<CraftCut>(`/api/projects/${encode(projectId)}/crafts/cut`),
  saveCraftCutVersion: (projectId: string, otio: unknown, label?: string) =>
    request<{ item: { id: string; label: string }; report: { clips: number; unmatched: string[]; pictureSound: number; kept?: { titles: number; graphics: number } } }>(
      `/api/projects/${encode(projectId)}/crafts/cut-version`, post({ otio, label })),
  craftSessions: (projectId: string) =>
    request<{ sessions: CraftSessionSummary[]; titles: CraftTitleSummary[] }>(`/api/projects/${encode(projectId)}/crafts/sessions`),
  craftSession: (projectId: string, mediaId: string) =>
    request<CraftSessionOpen>(`/api/projects/${encode(projectId)}/crafts/sessions/${encode(mediaId)}`),
  placeCraftTitle: (projectId: string, mediaId: string, startTime: number) =>
    request<{ clipId: string; track: string; startTime: number; duration: number; version: number }>(
      `/api/projects/${encode(projectId)}/crafts/place-title`, post({ mediaId, startTime })),
  craftStill: (projectId: string, mediaId: string, assetId: string) =>
    request<{ media: { id: string; url: string; name: string }; layeredSourceId: string | null; version: { id: string; label: string } | null }>(
      `/api/projects/${encode(projectId)}/crafts/still`, post({ mediaId, assetId })),
  craftShotStill: (projectId: string, payload: { mediaId: string; shotId: string; sourceJobId?: string; sourceCompositionId?: string }) =>
    request<ProjectJob>(`/api/projects/${encode(projectId)}/crafts/shot-still`, post(payload)),
  startCraftJob: (projectId: string, payload: CraftJobRequest) =>
    request<ProjectJob>(`/api/projects/${encode(projectId)}/crafts/jobs`, post(payload)),

  /* ---------------------------------------------------------------- jobs */
  jobs: (projectId: string) => request<{ jobs: ProjectJob[] }>(`/api/projects/${projectId}/jobs`),
  job: (projectId: string, jobId: string) => request<ProjectJob>(`/api/projects/${projectId}/jobs/${jobId}`),
  cancelJob: (projectId: string, jobId: string) => request(`/api/projects/${projectId}/jobs/${jobId}/cancel`, post()),
  resumeJob: (projectId: string, jobId: string) => request(`/api/projects/${projectId}/jobs/${jobId}/resume`, post()),
  retryJob: (projectId: string, jobId: string) => request(`/api/projects/${projectId}/jobs/${jobId}/retry`, post()),
  deleteJob: (projectId: string, jobId: string) => request(`/api/projects/${projectId}/jobs/${jobId}`, del()),
  scheduler: (options: { signal?: AbortSignal } = {}) => request<SchedulerBoard>('/api/scheduler', options),
  holdJob: (projectId: string, jobId: string) => request<ProjectJob>(`/api/projects/${encode(projectId)}/jobs/${encode(jobId)}/hold`, post()),
  setJobPriority: (projectId: string, jobId: string, priority: JobPriority) => request<ProjectJob>(`/api/projects/${encode(projectId)}/jobs/${encode(jobId)}/priority`, post({ priority })),
  setSchedulerLanes: (lanes: number) => request<{ lanes: number; updatedAt: string }>('/api/scheduler/lanes', put({ lanes })),
  queueGeneration: (projectId: string, payload: Record<string, unknown>) => {
    const headers: Record<string, string> = {};
    const key = payload?.idempotencyKey || payload?.clientRequestId;
    if (key) headers['Idempotency-Key'] = String(key);
    return request(`/api/projects/${projectId}/generations`, { method: 'POST', headers, ...json(payload) });
  },

  /* ----------------------------------------------------- creative engine */
  translatePrompt: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/prompts/translate`, post(payload)),
  compileShotPrompt: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/prompts/compile`, post(payload)),
  retrieveMemory: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/memory/retrieve`, post(payload)),
  scoreImpact: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/impact`, post(payload)),
  refreshPricing: (projectId: string, payload: unknown = {}) => request(`/api/projects/${projectId}/pricing/refresh`, post(payload)),
  simulateCost: (projectId: string, payload: unknown = {}) => request(`/api/projects/${projectId}/cost/simulate`, post(payload)),
  evaluateRisk: (projectId: string) => request(`/api/projects/${projectId}/risk/evaluate`, post()),
  forecastSchedule: (projectId: string) => request(`/api/projects/${projectId}/schedule/forecast`, post()),
  modelLearning: (projectId: string, query = '') =>
    request(`/api/projects/${projectId}/model-learning${query ? `?${new URLSearchParams(query)}` : ''}`),
  planAssembly: (projectId: string) => request(`/api/projects/${projectId}/assembly/plan`, post()),
  renderAssembly: (projectId: string, payload: unknown = {}) => request(`/api/projects/${projectId}/assembly/render`, post(payload)),
  composeAudioFromClip: (projectId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/timeline/audio-from-clip`, post(payload)),
  continuityScore: (projectId: string, payload: unknown = {}) => request(`/api/projects/${projectId}/continuity/score`, post(payload)),
  visualQC: (projectId: string, shotId: string, takeId: string, provider = '') =>
    request(`/api/projects/${projectId}/shots/${encode(shotId)}/takes/${encode(takeId)}/visual-qc`, post({ provider })),
  proposeRepair: (projectId: string, shotId: string, takeId: string) =>
    request(`/api/projects/${projectId}/shots/${encode(shotId)}/takes/${encode(takeId)}/repair`, post()),
  interpretShotIntent: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/intents/interpret`, post(payload)),
  interpretShotIntentFromImage: (projectId: string, payload: unknown) =>
    request(`/api/projects/${projectId}/intents/from-image`, post(payload)),
  traverseGraph: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/graph/traverse`, post(payload)),

  /* ---------------------------------------------------------- changesets */
  acceptChangeSet: (projectId: string, changeSetId: string) =>
    request(`/api/projects/${projectId}/changesets/${changeSetId}/accept`, post()),
  approveChangeSet: (projectId: string, changeSetId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/changesets/${changeSetId}/approve`, post(payload)),
  rejectChangeSet: (projectId: string, changeSetId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/changesets/${changeSetId}/reject`, post(payload)),
  commitChangeSet: (projectId: string, changeSetId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/changesets/${changeSetId}/commit`, post(payload)),

  /* --------------------------------------------------------------- media */
  media: (projectId: string) => request<{ media: MediaItem[] }>(`/api/projects/${projectId}/media`),
  retryMediaIngest: (projectId: string, mediaId: string) => request(`/api/projects/${projectId}/media/${mediaId}/retry-ingest`, post()),
  uploadMedia: (projectId: string, file: File, kind = 'reference') => {
    const body = new FormData();
    body.append('file', file);
    body.append('kind', kind);
    return request<MediaItem>(`/api/projects/${projectId}/media`, { method: 'POST', body });
  },
  /* ----------------------------------------------------- knowledge library */
  knowledgeDocuments: (projectId: string) =>
    request<{ documents: KnowledgeDocument[]; revision: number; version: number; limit: number }>(
      `/api/projects/${encode(projectId)}/knowledge/documents`
    ),
  uploadKnowledgeDocument: (projectId: string, file: File) => {
    const body = new FormData();
    body.append('file', file);
    return request<{ document: KnowledgeDocument; version: number }>(
      `/api/projects/${encode(projectId)}/knowledge/documents`,
      { method: 'POST', body }
    );
  },
  removeKnowledgeDocument: (projectId: string, documentId: string) =>
    request<{ removed: string; version: number; note: string }>(
      `/api/projects/${encode(projectId)}/knowledge/documents/${encode(documentId)}`,
      { method: 'DELETE' }
    ),
  searchKnowledge: (projectId: string, query: string, documentIds: string[] = []) =>
    request<{ query: string; results: KnowledgePassage[]; searchedDocuments: number; indexedPassages: number }>(
      `/api/projects/${encode(projectId)}/knowledge/search`,
      post({ query, documentIds })
    ),
  spatialLocations: (projectId: string) =>
    request<{ locations: SpatialLocation[] }>(`/api/projects/${encode(projectId)}/spatial-locations`),
  createSpatialLocation: (projectId: string, payload: { worldAssetId: string; name?: string }) =>
    request<SpatialLocationResult>(`/api/projects/${encode(projectId)}/spatial-locations`, post(payload)),
  updateSpatialLocation: (projectId: string, locationId: string, payload: { name?: string; activeAnchorId?: string }) =>
    request<SpatialLocationResult>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}`, patch(payload)),
  addSpatialAnchor: (projectId: string, locationId: string, payload: Record<string, unknown>) =>
    request<SpatialLocationResult & { anchor: SpatialAnchor }>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/anchors`, post(payload)),
  updateSpatialAnchor: (projectId: string, locationId: string, anchorId: string, payload: Record<string, unknown>) =>
    request<SpatialLocationResult & { anchor: SpatialAnchor }>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/anchors/${encode(anchorId)}`, patch(payload)),
  spatialAnchorQc: (projectId: string, locationId: string, anchorId: string, payload: { width: number; height: number; seamScore?: number; holeRatio?: number }) =>
    request<SpatialLocationResult & { anchor: SpatialAnchor }>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/anchors/${encode(anchorId)}/qc`, post(payload)),
  saveSpatialFrame: (projectId: string, locationId: string, payload: Record<string, unknown>) =>
    request<SpatialLocationResult & { frame: SpatialFrame }>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/frames`, post(payload)),
  updateSpatialFrame: (projectId: string, locationId: string, frameId: string, payload: Record<string, unknown>) =>
    request<SpatialLocationResult & { frame: SpatialFrame }>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/frames/${encode(frameId)}`, patch(payload)),
  deleteSpatialFrame: (projectId: string, locationId: string, frameId: string) =>
    request<SpatialLocationResult>(`/api/projects/${encode(projectId)}/spatial-locations/${encode(locationId)}/frames/${encode(frameId)}`, del()),
  extractAudio: (projectId: string, payload: unknown = {}) =>
    request(`/api/projects/${encode(projectId)}/editor/extract-audio`, post(payload)),
  assembleApproved: (projectId: string) =>
    request<{ applied: boolean; reason?: string; added?: number; project?: Project }>(`/api/projects/${encode(projectId)}/editor/assemble-approved`, post({})),
  glTransitions: (query?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (limit) params.set('limit', String(limit));
    const suffix = params.toString() ? `?${params}` : '';
    return request(`/api/gl-transitions${suffix}`);
  },
  glTransition: (name: string) => request(`/api/gl-transitions/${encode(name)}`),

  /* -------------------------------------------------------------- canvas */
  canvasBoards: (projectId: string) => request<{ boards: CanvasBoard[] }>(`/api/projects/${encode(projectId)}/canvas/boards`),
  createCanvasBoard: (projectId: string, payload: { name: string; scopeType?: string; scopeId?: string }) =>
    request<{ board: CanvasBoard }>(`/api/projects/${encode(projectId)}/canvas/boards`, post(payload)),
  canvasBoard: (projectId: string, boardId: string) =>
    request<{ board: CanvasBoardContent }>(`/api/projects/${encode(projectId)}/canvas/boards/${encode(boardId)}`),
  saveCanvasBoard: (projectId: string, boardId: string, payload: { elements: unknown[]; appState: Record<string, unknown>; files: Record<string, unknown>; clientId: string }) =>
    request<{ ok: true; updatedAt: string }>(`/api/projects/${encode(projectId)}/canvas/boards/${encode(boardId)}`, put(payload)),
  renameCanvasBoard: (projectId: string, boardId: string, name: string) =>
    request(`/api/projects/${encode(projectId)}/canvas/boards/${encode(boardId)}`, patch({ name })),
  archiveCanvasBoard: (projectId: string, boardId: string) =>
    request(`/api/projects/${encode(projectId)}/canvas/boards/${encode(boardId)}`, { method: 'DELETE' }),
  canvasComments: (projectId: string, resourceType: string, resourceId: string) =>
    request<{ comments: CanvasComment[] }>(`/api/projects/${encode(projectId)}/canvas/comments?resourceType=${encode(resourceType)}&resourceId=${encode(resourceId)}`),
  createCanvasComment: (projectId: string, payload: { boardId: string; resourceType: string; resourceId: string; text: string }) =>
    request<{ comment: CanvasComment }>(`/api/projects/${encode(projectId)}/canvas/comments`, post(payload)),
  canvasReactions: (projectId: string, resourceType: string, resourceId: string) =>
    request<{ reactions: CanvasReaction[] }>(`/api/projects/${encode(projectId)}/canvas/reactions?resourceType=${encode(resourceType)}&resourceId=${encode(resourceId)}`),
  toggleCanvasReaction: (projectId: string, payload: { boardId: string; resourceType: string; resourceId: string; emoji: string }) =>
    request<{ added: boolean }>(`/api/projects/${encode(projectId)}/canvas/reactions`, post(payload)),
  canvasCreate: (projectId: string, card: unknown) => request(`/api/projects/${projectId}/canvas`, post(card)),
  canvasUpdate: (projectId: string, cardId: string, changes: unknown) =>
    request(`/api/projects/${projectId}/canvas/${cardId}`, patch(changes)),
  canvasLock: (projectId: string, cardId: string) => request(`/api/projects/${projectId}/canvas/${cardId}/lock`, post()),
  canvasUnlock: (projectId: string, cardId: string) => request(`/api/projects/${projectId}/canvas/${cardId}/unlock`, post()),
  canvasDelete: (projectId: string, cardId: string) => request(`/api/projects/${projectId}/canvas/${cardId}`, del()),
  comment: (projectId: string, comment: unknown) => request(`/api/projects/${projectId}/comments`, post(comment)),

  /* --------------------------------------------------------- collaborate */
  projectAccess: (projectId: string) => request(`/api/projects/${projectId}/access`),
  saveProjectAccess: (projectId: string, userId: string, role: string) =>
    request(`/api/projects/${projectId}/access/${userId}`, put({ role })),
  collaborators: (projectId: string) =>
    request<{ members: ProjectCollaborator[]; invitations: { id: string; email: string; role: string; expiresAt?: string }[] }>(`/api/projects/${projectId}/collaborators`),
  listAssignments: (projectId: string) => request(`/api/projects/${projectId}/assignments`),
  assignWork: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/assignments`, put(payload)),
  unassignWork: (projectId: string, payload: unknown) =>
    request(`/api/projects/${projectId}/assignments`, { method: 'DELETE', ...json(payload) }),
  checkoutWork: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/assignments/checkout`, post(payload)),
  releaseWork: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/assignments/release`, post(payload)),
  completeWork: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/assignments/complete`, post(payload)),
  studioMembersForProject: (projectId: string) => request<{ members: StudioMember[] }>(`/api/projects/${encode(projectId)}/studio-members`),
  projectInvitations: (projectId: string) => request<{ invitations: ProjectInvitation[] }>(`/api/projects/${encode(projectId)}/invitations`),
  createProjectInvitation: (projectId: string, payload: { userId: string; role: string }) =>
    request<{ invitation: ProjectInvitation }>(`/api/projects/${encode(projectId)}/invitations`, post(payload)),
  revokeProjectInvitation: (projectId: string, invitationId: string) =>
    request(`/api/projects/${encode(projectId)}/invitations/${encode(invitationId)}/revoke`, post()),
  studios: () => request<{ studios: StudioMembership[]; invitations: StudioInvitation[] }>('/api/account/studios'),
  switchStudio: (studioId: string) =>
    request<{ ok: true; studio: { id: string; name: string; role: string } }>('/api/account/studio', post({ studioId })),
  acceptStudioInvitation: (invitationId: string) =>
    request<{ ok: true; studio: { id: string; name: string; role: string } }>(`/api/account/invitations/${encode(invitationId)}/accept`, post()),
  declineStudioInvitation: (invitationId: string) =>
    request<{ ok: true }>(`/api/account/invitations/${encode(invitationId)}/decline`, post()),
  myProjectInvitations: () => request<{ invitations: ProjectInvitation[] }>('/api/project-invitations/mine'),
  acceptProjectInvitation: (invitationId: string) =>
    request<{ ok: true; projectId: string }>(`/api/project-invitations/${encode(invitationId)}/accept`, post()),
  declineProjectInvitation: (invitationId: string) =>
    request(`/api/project-invitations/${encode(invitationId)}/decline`, post()),
  collabLocks: (projectId: string) => request(`/api/projects/${projectId}/collab/locks`),
  claimCollabLock: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/collab/locks`, post(payload)),
  releaseCollabLock: (projectId: string, lockId: string) => request(`/api/projects/${projectId}/collab/locks/${lockId}`, del()),
  renewCollabLock: (projectId: string, lockId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/collab/locks/${encode(lockId)}/renew`, post(payload)),
  activity: (projectId: string) => request(`/api/projects/${projectId}/activity`),

  /* -------------------------------------------------------------- review */
  reviews: (projectId: string) => request<{ presentations: ReviewPresentation[] }>(`/api/projects/${encode(projectId)}/reviews`),
  review: (projectId: string, presentationId: string) => request(`/api/projects/${projectId}/reviews/${presentationId}`),
  createReview: (projectId: string, payload: unknown) => request<{ presentation: { id: string }; reviewUrl: string; items: Array<{ id: string; mediaId: string; label?: string }> }>(`/api/projects/${encode(projectId)}/reviews`, post(payload)),
  reviewLinks: (projectId: string, presentationId: string) =>
    request<{ links: ReviewLink[] }>(`/api/projects/${encode(projectId)}/reviews/${encode(presentationId)}/links`),
  createReviewLink: (projectId: string, presentationId: string, payload: unknown) =>
    request<{ reviewUrl: string; token: string; link: Record<string, unknown> }>(`/api/projects/${encode(projectId)}/reviews/${encode(presentationId)}/links`, post(payload)),
  revokeReviewLink: (projectId: string, presentationId: string, linkId: string) =>
    request<{ ok: true; status: string }>(`/api/projects/${encode(projectId)}/reviews/${encode(presentationId)}/links/${encode(linkId)}/revoke`, post()),
  pinLatestReviewItem: (projectId: string, presentationId: string, itemId: string) =>
    request(`/api/projects/${encode(projectId)}/reviews/${encode(presentationId)}/items/${encode(itemId)}/pin-latest`, post()),
  revokeReview: (projectId: string, presentationId: string) =>
    request(`/api/projects/${projectId}/reviews/${presentationId}/revoke`, post()),
  rotateReviewLink: (projectId: string, presentationId: string) =>
    request<{ reviewUrl: string }>(`/api/projects/${encode(projectId)}/reviews/${encode(presentationId)}/rotate-link`, post()),
  setReviewCommentStatus: (projectId: string, presentationId: string, commentId: string, status: string) =>
    request(`/api/projects/${projectId}/reviews/${presentationId}/comments/${commentId}`, patch({ status })),
  supersedeReviewItem: (projectId: string, presentationId: string, itemId: string, payload: unknown) =>
    request(`/api/projects/${projectId}/reviews/${presentationId}/items/${itemId}/supersede`, post(payload)),
  recommendMediaModel: (projectId: string, payload: unknown = {}) =>
    request<MediaModelRecommendation>(`/api/projects/${encode(projectId)}/media-models/recommend`, post(payload)),
  approveAsset: (projectId: string, assetId: string, payload: unknown = {}) =>
    request(`/api/projects/${projectId}/assets/${assetId}/approve`, post(payload)),
  approveAssetVariant: (projectId: string, assetId: string, variantId: string, payload: unknown) =>
    request(`/api/projects/${encode(projectId)}/assets/${encode(assetId)}/variants/${encode(variantId)}/approve`, post(payload)),
  assetVersion: (projectId: string, assetId: string, payload: unknown) =>
    request(`/api/projects/${projectId}/assets/${assetId}/versions`, post(payload)),

  /* ------------------------------------------------------- notifications */
  notifications: () => request<{ notifications: NotificationItem[] }>('/api/notifications'),
  readNotification: (notificationId: string) => request(`/api/notifications/${notificationId}/read`, post()),
  readAllNotifications: () => request('/api/notifications/read-all', post()),
  queueNotificationDigest: () => request('/api/notifications/digest', post()),
  notificationDeliveries: () => request<{ deliveries: { id: string; template: string; status: string; attempts: number; sent_at?: string | null }[] }>('/api/notifications/deliveries'),

  /* -------------------------------------------------------------- studio */
  platformStatus: () => request<RuntimeStatus>('/api/platform-status'),
  runPlatformSteward: () => request('/api/platform-steward/run', post()),
  vfxDepthStatus: () => request('/api/vfx/depth/status'),
  vfxLocalAiStatus: (start = false) => request(`/api/vfx/local-ai/status${start ? '?start=1' : ''}`),
  extractVfxDepth: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/vfx/depth`, post(payload)),
  exportVfxLayer: (projectId: string, payload: unknown) => request(`/api/projects/${projectId}/vfx/export-layer`, post(payload)),
  ingestStudioEpisode: (payload: unknown) => request('/api/studio-knowledge/episodes', post(payload)),

  /* --------------------------------------------------------------- admin */
  usage: (options: RequestInit = {}) => request<UsageSummary>('/api/admin/usage', options),
  pricing: (options: RequestInit = {}) => request<PricingData>('/api/billing/pricing', options),
  chooseFreePlan: () => request<{ plan: string }>('/api/billing/plan/free', post({})),
  createRazorpayOrder: (purchase: PurchaseRequest) => request<RazorpayOrder>('/api/billing/razorpay/order', post(purchase)),
  verifyRazorpayPayment: (payload: { orderId: string; paymentId: string; signature: string }) =>
    request<RazorpayVerification>('/api/billing/razorpay/verify', post(payload)),
  team: () => request('/api/admin/team'),
  inviteTeamMember: (payload: unknown) => request('/api/admin/invitations', post(payload)),
  updateTeamRole: (userId: string, role: string) => request(`/api/admin/team/${encode(userId)}`, patch({ role })),
  removeTeamMember: (userId: string) => request(`/api/admin/team/${encode(userId)}`, del()),
  revokeTeamInvitation: (invitationId: string) => request(`/api/admin/invitations/${encode(invitationId)}/revoke`, post()),
  saveProvider: (provider: string, credentials: unknown) => request(`/api/admin/providers/${provider}`, put({ credentials })),
  testProvider: (provider: string) => request(`/api/admin/providers/${provider}/test`, post()),
  getAnyfastCatalog: () => request('/api/providers/anyfast/catalog'),
  syncAnyfastCatalog: () => request('/api/admin/providers/anyfast/sync', post()),
  stagingReadiness: () => request('/api/admin/staging-readiness')
};

export type Api = typeof api;

/** Authenticated binary response (client delivery ZIPs). No billable work is performed. */
export async function requestBlob(path: string, options: RequestInit = {}): Promise<Blob> {
  const headers: Record<string, string> = { ...campaignLeaseHeader(path), 'Content-Type': 'application/json', ...((options.headers as Record<string,string>) || {}) };
  if (options.method && !['GET','HEAD'].includes(options.method)) { const token = csrfToken(); if (token) headers['X-CSRF-Token'] = token; }
  const response = await fetch(path, { credentials: 'same-origin', ...options, headers });
  if (!response.ok) { const body = await response.json().catch(() => ({})); throw new ApiError(body.error || body.message || 'Delivery package failed.', response.status); }
  if (!response.headers.get('content-type')?.includes('application/zip')) throw new ApiError('The server did not return a delivery ZIP.', 502);
  return response.blob();
}
